import React, { useMemo } from 'react';

import { Numeric } from '@swyftx/aviary/atoms/Typography';
import { NumericProps } from '@swyftx/aviary/atoms/Typography/Numeric/Numeric.styles';
import { MarginInstrument } from '@swyftx/margin/build/types';

import { Big } from '@shared/safe-big';
import { formatCurrency } from '@shared/utils';

import { observer } from 'mobx-react-lite';
import { useMarginPrices } from 'src/lib/margin/hooks/prices';

type Props = {
  market: MarginInstrument;
  variant: 'bid' | 'ask' | 'dailyChange';
} & NumericProps;

const MarginInstrumentDetailsText: React.FC<Props> = observer(({ market, variant, color, size, weight }) => {
  const { getMarketPriceByInstrument } = useMarginPrices();
  const price = useMemo(() => getMarketPriceByInstrument(market), [getMarketPriceByInstrument, market]);

  const content = useMemo(() => {
    if (!price) return undefined;

    switch (variant) {
      case 'bid':
        return formatCurrency(price.bid, undefined, { priceScale: market.precision });
      case 'ask':
        return formatCurrency(price.ask, undefined, { priceScale: market.precision });
      case 'dailyChange':
        return `${Big(price.bidChangeYesterday).toFixed(2)}%`;
    }
  }, [market.precision, price, variant]);

  const numericColor = useMemo((): NumericProps['color'] => {
    if (!price) return undefined;
    if (color) return color;

    switch (variant) {
      case 'bid':
        return 'primary';
      case 'ask':
        return 'primary';
      case 'dailyChange':
        return Big(price.bidChangeYesterday).gt(0) ? 'success' : 'error';
    }
  }, [color, price, variant]);

  return (
    <Numeric color={numericColor} size={size} weight={weight} loading={!content}>
      {content || '0.00'}
    </Numeric>
  );
});

export { MarginInstrumentDetailsText };
